import { Injectable, inject } from '@angular/core';
import {
  AppState,
  AuthService,
  CacheService,
  CardsService,
  PageService,
  PillarType,
  SettingsService,
} from '@gallaghercp/core-frontend';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot } from '@angular/router';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class ClientService implements CanActivate {
  protected auth = inject(AuthService);
  protected router = inject(Router);
  protected pages = inject(PageService);
  protected cache = inject(CacheService);
  protected settings = inject(SettingsService);

  DEFAULT = {
    name: 'University of Houston',
    login: 'Welcome',
    welcome: '',
    support: 'Need help signing in?',
    uid: '',
    uidHelper: false,
    dobFormat: 'DD/MM/YYYY',
  };

  provider: any;

  private _selectedCampus = new BehaviorSubject<string | null>(null); // Default value: null
  selectedCampus$ = this._selectedCampus.asObservable();

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | Promise<boolean> | boolean {
    this.auth.setRedirectUrl(state.url);
    if (!this.auth.authResumed) {
      this.auth.resume();
    }

    return this.auth.isResumed.pipe(
      take(1),
      switchMap(() => {
        if (this.auth.isAuthenticated()) {
          return of(true);
        }
        return this.getAccessType().pipe(
          map((type: any) => {
            if (type === 'public' && state.url !== '/profile') {
              return true;
            }
            this.router.navigate(['/login']);
            return false;
          }),
        );
      }),
    );
  }

  getConfiguration() {
    this.provider = this.auth.serviceProvider;
    return this.provider && this.provider.configuration ? this.provider.configuration : {};
  }

  getAccessType(): Observable<AppState> {
    return this.settings.getAuthenticationType().pipe(
      map((authenticationType: any) => {
        if (authenticationType !== undefined) {
          return authenticationType;
        }
        return this.getConfiguration().accessType || 'individual';
      }),
    );
  }

  getSiteName() {
    return this.getConfiguration().name || this.DEFAULT.name;
  }

  getUserNotifications(clear?: boolean) {
    return clear
      ? this.cache.getListThroughCache('user-notifications', { interacted: false }, {}, Date.now() as unknown as string)
      : this.cache.getListThroughCache('user-notifications', {
          interacted: false,
        });
  }

  getPageCategories() {
    return this.pages.getListThroughCache('page-categories').pipe(
      map((categories: any[any]) => {
        const allPages: any[] = [];
        categories.forEach((category: { pageCategories: [] }) => {
          if (category.pageCategories) {
            category.pageCategories.forEach((page: { pageId: number }) => {
              if (allPages.filter((e: { id: number }) => e.id === page.pageId).length === 0) {
                allPages.push(page);
              }
            });
          }
        });
        if (categories.filter((e: { name: string }) => e.name === 'All').length === 0) {
          categories.unshift({
            id: 0,
            name: 'All',
            pageCategories: allPages,
          });
        }
        return categories;
      }),
    );
  }

  getPillars() {
    return this.cache.getListThroughCache('page-categories').pipe(
      map((categories: any[any]) => {
        const allPages: any[] = [];
        const pillars = categories.filter((pillar: PillarType) => {
          return CardsService.PILLARS.includes(pillar.name);
        });
        pillars.forEach((category: { pageCategories: [] }) => {
          if (category.pageCategories) {
            category.pageCategories.forEach((page: { url: string }) => {
              if (allPages.filter((e: { url: string }) => e.url === page.url).length === 0) {
                allPages.push(page);
              }
            });
          }
        });
        pillars.forEach((pillar: any) => {
          pillar.pillarClass = pillar.name
            .replace(new RegExp(' ', 'g'), '-')
            .replace(new RegExp('/', 'g'), '-')
            .toLowerCase();
        });

        return pillars;
      }),
    );
  }
}
